import React from "react";
import { AlertTriangle } from "lucide-react";

import Callout from "@/components/ui/Callout";
import SettingRow from "@/components/shared/SettingRow";
import Switch from "@/components/ui/Switch";

/**
 * One module row with a toggle, for the fleet-wide kill switch.
 *
 * **Availability only.** This row switches "is this working right now?" and
 * never "is this in their plan?". Entitlement is a tree, so it renders through
 * `pages/super-admin/tenant/components/ModuleSelector.jsx` instead — a flat
 * row cannot show a child that waits on its parent.
 *
 * The two axes are never merged into one control: "they own it but it is down"
 * is the state that most needs to be visible, and one boolean would throw it
 * away.
 *
 * The row itself is `<SettingRow>` and the toggle is `<Switch>`, both shared
 * with org settings and prompt safety — three screens, one setting row.
 */
const ModuleToggleRow = ({ module, checked, disabled, onToggle }) => (
  <SettingRow
    title={module.name}
    description={module.description}
    // No "internal" badge. Every module this page lists is killable, and the
    // registry has no internal-only entry any more — the flag it read
    // (`configurable`) was removed with the redesign.
    badges={<code className="rounded bg-tone-slate-soft px-1.5 py-0.5 text-[11px] text-text-secondary">{module.code}</code>}
    note={
      module.killImpact ? (
        <Callout icon={AlertTriangle} size="sm">
          <span className="font-medium text-text-primary">If switched off: </span>
          {module.killImpact}
        </Callout>
      ) : null
    }
    control={<Switch checked={checked} disabled={disabled} onToggle={onToggle} label={module.name} />}
  />
);

export default ModuleToggleRow;
